/**
 * Prefetch on Hover - Performance Optimization
 * Preloads pages when users hover over links, making navigation feel instant
 *
 * Features:
 * - Prefetches on mouseenter (hover) and touchstart
 * - Only prefetches same-origin Shopify links
 * - Prevents duplicate prefetches
 * - Ignores cart, checkout, and external links
 * - Minimal performance overhead
 */

(function() {
  'use strict';

  // Track prefetched URLs to avoid duplicates
  const prefetched = new Set();

  // Ignore these URL patterns
  const ignorePatterns = [
    /\/cart/i,
    /\/checkout/i,
    /\/account/i,
    /\/admin/i,
    /\.pdf$/i,
    /\.zip$/i,
    /mailto:/i,
    /tel:/i,
    /#/
  ];

  /**
   * Check if URL should be prefetched
   */
  function shouldPrefetch(url) {
    // Must be same origin
    if (!url || url.origin !== location.origin) {
      return false;
    }

    // Must be HTTP/HTTPS
    if (!url.protocol.startsWith('http')) {
      return false;
    }

    // Don't prefetch current page
    if (url.href === location.href) {
      return false;
    }

    // Already prefetched
    if (prefetched.has(url.href)) {
      return false;
    }

    // Check ignore patterns
    for (const pattern of ignorePatterns) {
      if (pattern.test(url.href)) {
        return false;
      }
    }

    return true;
  }

  /**
   * Create and append prefetch link
   */
  function prefetch(url) {
    if (!shouldPrefetch(url)) {
      return;
    }

    // Mark as prefetched
    prefetched.add(url.href);

    // Create prefetch link element
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url.href;
    link.as = 'document';

    // Optional: Add event listeners for debugging
    // link.addEventListener('load', () => console.log('Prefetched:', url.href));
    // link.addEventListener('error', () => console.log('Prefetch failed:', url.href));

    document.head.appendChild(link);
  }

  /**
   * Handle link interaction (hover or touch)
   */
  function handleLinkInteraction(event) {
    const link = event.target.closest('a');

    if (!link || !link.href) {
      return;
    }

    try {
      const url = new URL(link.href);
      prefetch(url);
    } catch (e) {
      // Invalid URL, ignore
    }
  }

  /**
   * Initialize prefetch on hover
   */
  function init() {
    // Check for browser support
    const supportsPrefetch = document.createElement('link').relList?.supports?.('prefetch');

    if (!supportsPrefetch) {
      return; // Prefetch not supported
    }

    // Use event delegation for better performance
    // Listen at document level for all link hovers
    document.addEventListener('mouseover', handleLinkInteraction, {
      passive: true,
      capture: true
    });

    // Also handle touchstart for mobile (before tap)
    document.addEventListener('touchstart', handleLinkInteraction, {
      passive: true,
      capture: true
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
